import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, of, combineLatest } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppConstants } from '../../app.constants';
import { AuthenticationsService } from '../../modules/authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../../modules/authentications/dtos/authentication-login-response';
import {
	OrgBeneficiary,
	OrgBeneficiaryChip,
} from '../../sanus/organizations/models/beneficiaries.model';
import {
	clearBulkFileUrl,
	clearOrgBeneficiariesDataURL,
	clearBulkUploadResult,
	fetchOrgBeneficiariesDataURLStart,
	fetchBulkActionFileUrlStart,
	uploadBulkActionFileStart,
	fetchOrgBeneficiariesClear,
} from '../../sanus/organizations/state/organizations.actions';
import {
	selectOrgBeneficiariesDataURL,
	selectOrgUploadFileResult,
	selectXlsxUrl,
	selectBulkActionId,
} from '../../sanus/organizations/state/organizations.selector';
import { PaginationArgsInterface } from '../../shared/components/pagination/models/pagination-args.interface';
import { PaginationArgs } from '../../shared/components/pagination/models/pagination-args.model';
import { FileUploadResult } from '../../shared/enums/file-upload-result.enum';
import { SearchCase } from '../../shared/enums/search-case.enum';
import { SortingType } from '../../shared/enums/sorting-type.enum';
import { TemplateFileType } from '../../shared/enums/template-file.enum';
import { TypeKinship } from '../../shared/enums/type-kinship.enum';
import { CpfUtils } from '../../utils/cpf-utils';
import { DataTypeUtils } from '../../utils/data-type.util';
import { PhoneUtils } from '../../utils/phone-utils';

import { BeneficiariesService } from './beneficiaries.service';
import { BeneficiariesFieldOrdination } from './enums/beneficiaries-field-ordination.enum';
import { BeneficiaryStatusFilter } from './enums/beneficiary-status-filter.enum';
import { BeneficiaryPendencyStatusTranslatedEnum } from './enums/beneficiary-status-translated';
import { BeneficiaryPendency } from './enums/pendency.enum';
import { AddDependentModalComponent } from './modals/add-dependent-modal/add-dependent-modal.component';
import { BeneficiaryTypeModalComponent } from './modals/beneficiary-type-modal/beneficiary-type-modal.component';
import { BulkActionDialogComponent } from './modals/bulk-action-upload-dialog/bulk-action-upload-dialog.component';
import { ErrorDialogComponent } from './modals/error-dialog/error-dialog.component';
import { PendenciesModalComponent } from './modals/pendencies-modal/pendencies-modal.component';
import { ColumnKey } from './types/column-keys.type';

@Component({
	selector: 'app-beneficiaries',
	styleUrls: ['./beneficiaries.component.scss'],
	templateUrl: './beneficiaries.component.html',
})
export class BeneficiariesComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	@ViewChild('fileInputExclusion') fileInputExclusion?: ElementRef;
	@ViewChild('fileInputInclusion') fileInputInclusion?: ElementRef;
	@ViewChild('fileInputTransfer') fileInputTransfer?: ElementRef;
	@ViewChild('fileInputUpdate') fileInputUpdate?: ElementRef;
	@ViewChild(MatPaginator) paginator: MatPaginator | null = null;
	@ViewChild(MatSort) sort!: MatSort;

	DATE_COLUMN_POSITION = 4;
	actionType = TemplateFileType;
	activeTab = BeneficiaryStatusFilter.ACTIVE;
	beneChips: OrgBeneficiaryChip[] = [
		{
			label: 'Aberto',
			mobileLabel: 'Em aberto',
			status: BeneficiaryPendency.FIELDS_REQUIRED,
		},
		{
			label: 'Processando',
			mobileLabel: 'Proc.',
			status: BeneficiaryPendency.PROCESSING_IN_CARRIER,
		},
		{
			label: 'Pendência RH',
			mobileLabel: 'Pend. RH',
			status: BeneficiaryPendency.PENDENCY_WITH_HR,
		},
		{
			label: 'Completo',
			mobileLabel: 'Completo',
			status: null,
		},
	];
	beneTabs = [
		{ label: 'Ativos', path: BeneficiaryStatusFilter.ACTIVE },
		{ label: 'Inclusões', path: BeneficiaryStatusFilter.INCLUSION },
		{
			label: 'Exclusões',
			path: BeneficiaryStatusFilter.EXCLUSION,
		},
		{ label: 'Inativos', path: BeneficiaryStatusFilter.INACTIVE },
	];
	beneficiaries = new MatTableDataSource<OrgBeneficiary>([]);
	beneficiariesFieldOrdinationEnum = BeneficiariesFieldOrdination;
	beneficiariesQuery = {
		select: {
			id: true,
			countDependents: true,
			inactived_at: true,
			pendency: true,
			status: true,
			type_kinship: true,
			user: {
				select: {
					cpf: true,
					created_at: true,
					name: true,
					phone: true,
				},
			},
		},
	};
	beneficiaryStatusEnum = BeneficiaryStatusFilter;
	beneficiaryStatusFilter = BeneficiaryStatusFilter;
	commonColumns: ColumnKey[] = [
		'status',
		'name',
		'phone',
		'cpf',
		'type',
		'dependentCount',
	];
	cpfMask = CpfUtils.mask;
	dialogRef!: MatDialogRef<
		| AddDependentModalComponent
		| BeneficiaryTypeModalComponent
		| PendenciesModalComponent
	>;
	getWithSearchTerm: boolean = false;
	inputId: string = String(Math.random());
	insuranceStatus: string = '';
	isLoading: boolean = true;
	isSanusMode: boolean = false;
	isSearchButtonDisabled: boolean = true;
	nullPlaceholder: string = DataTypeUtils.string.getNullPlaceholder();
	ordination: SortingType = SortingType.ASCENDING;
	// TODO investigar multiplos downloads
	preventMultipleDownloads = false;
	searchButton: boolean = true;
	searchForm: UntypedFormGroup;
	selectedLabel: string = 'ativo';
	sortedField: BeneficiariesFieldOrdination | undefined =
		BeneficiariesFieldOrdination.NAME;
	status: string = '';
	tableColumns: Record<string, ColumnKey[]> = {
		activeAndInactive: [
			...this.commonColumns.slice(0, this.DATE_COLUMN_POSITION), // 'status', 'name', 'phone', 'cpf'
			'createdAt', // Campo específico para activeAndInactive
			...this.commonColumns.slice(this.DATE_COLUMN_POSITION), // 'type', 'dependentCount'
		],
		exclusion: [
			...this.commonColumns.slice(0, this.DATE_COLUMN_POSITION), // 'status', 'name', 'phone', 'cpf'
			'exclusionDate', // Campo específico para exclusion
			...this.commonColumns.slice(this.DATE_COLUMN_POSITION), // 'type', 'dependentCount'
		],
		inactive: [
			...this.commonColumns.slice(0, this.DATE_COLUMN_POSITION), // 'status', 'name', 'phone', 'cpf'
			'removedFromInsurance', // Campo específico para inactive
			...this.commonColumns.slice(this.DATE_COLUMN_POSITION), // 'type', 'dependentCount'
		],
	};
	tableColumnsShown: string[] = this.tableColumns.active;
	tablePerPage: number = AppConstants.pagination.itemsPerPage;
	tableTotalItems: number = 0;
	tableTotalPages: number = 0;
	userEmail: string = '';
	private orgId: string = '';
	private readonly sanusId: string = AppConstants.organization.id;
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly authenticationsService: AuthenticationsService,
		private readonly beneficiariesService: BeneficiariesService,
		private readonly router: Router,
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly store: Store,
		private readonly liveAnnouncer: LiveAnnouncer,
	) {
		this.searchForm = this.fb.group({
			sort: ['asc'],
			term: [null],
		});
	}

	clearFileInputs(): void {
		if (this.fileInputInclusion) {
			this.fileInputInclusion.nativeElement.value = '';
		}

		if (this.fileInputUpdate) {
			this.fileInputUpdate.nativeElement.value = '';
		}

		if (this.fileInputTransfer) {
			this.fileInputTransfer.nativeElement.value = '';
		}

		if (this.fileInputExclusion) {
			this.fileInputExclusion.nativeElement.value = '';
		}
	}

	clearPaginator(): void {
		if (this.paginator?.pageIndex) {
			this.paginator.pageIndex = 0;
		}
	}

	downloadFile(url: string): void {
		this.preventMultipleDownloads = true;
		const link = document.createElement('a');
		link.setAttribute('target', '_blank');
		link.setAttribute('href', url);
		link.setAttribute('download', 'file_name.xlsx');
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	getBeneficiaries({
		hideSearchBtn = false,
	}: {
		hideSearchBtn?: boolean;
	} = {}): void {
		const searchObj = this.searchForm.value;
		const isActiveOrInclusion =
			this.activeTab === BeneficiaryStatusFilter.ACTIVE ||
			this.activeTab === BeneficiaryStatusFilter.INCLUSION;

		const where: any = {
			insuranceStatus: this.activeTab.toUpperCase(),
			user: {
				organizations: {
					some: { id: this.orgId },
				},
			},
		};

		if (searchObj.term) {
			this.getWithSearchTerm = searchObj.term?.length;
			searchObj.term = searchObj.term.trim();

			const searchTerm = searchObj.term.replace(/[-.]/g, '');
			const isNumber = /^\d+$/.test(searchTerm);

			if (isNumber) {
				where.user.cpf = {
					contains: searchTerm.trim(),
				};
			} else if (isActiveOrInclusion) {
				where.user.name = {
					contains: searchObj.term,
					mode: SearchCase.INSENSITIVE,
				};
			} else {
				where.name = {
					contains: searchObj.term,
					mode: SearchCase.INSENSITIVE,
				};
			}
		}

		if (hideSearchBtn) {
			this.searchButton = false;
		}

		const query: PaginationArgsInterface = {
			limit: this.tablePerPage,
			orderBy: this.getOrderByClausule(),
			page: (this.paginator?.pageIndex || 0) + 1,
			select: this.beneficiariesQuery.select,
			where: where,
		};

		if (
			!isActiveOrInclusion &&
			this.activeTab !== BeneficiaryStatusFilter.EXCLUSION
		) {
			query.select = {};
		}

		this.beneficiaries = new MatTableDataSource<OrgBeneficiary>([]);
		this.isLoading = true;

		this.beneficiariesService
			.getBeneficiaries(new PaginationArgs(query))
			.pipe(
				catchError(({ error }) => {
					this.isLoading = false;
					this.showErrorMessage(
						error.message || 'Um erro ocorreu fazer a busca de beneficiários',
					);

					return of({ count: 0, result: [], totalPages: 0 });
				}),
			)
			.subscribe((data) => {
				this.beneficiaries = new MatTableDataSource<OrgBeneficiary>(
					data.result,
				);

				this.tableTotalPages = data.totalPages;
				this.isLoading = false;
				this.tableTotalItems = data.count;
			});
	}

	getBeneficiaryPendencyStatus(
		status: string,
	): BeneficiaryPendencyStatusTranslatedEnum {
		const pendencyMap: {
			[key: string]: BeneficiaryPendencyStatusTranslatedEnum;
		} = {
			active: BeneficiaryPendencyStatusTranslatedEnum.ACTIVE,
			exclusion: BeneficiaryPendencyStatusTranslatedEnum.EXCLUSION,
			inactive: BeneficiaryPendencyStatusTranslatedEnum.INACTIVE,
			inclusion: BeneficiaryPendencyStatusTranslatedEnum.INCLUSION,
		};

		this.setTabsBeneficiary(pendencyMap[status]);

		return pendencyMap[status];
	}

	getOrderByClausule(): Object {
		if (this.ordination === SortingType.UNDEFINED) {
			return {
				createdAt: SortingType.DESCENDING,
			};
		}

		switch (this.sortedField) {
			case undefined: {
				return {};
			}

			case BeneficiariesFieldOrdination.CREATION_DATE: {
				return { createdAt: this.ordination };
			}

			case BeneficiariesFieldOrdination.NAME: {
				return this.getOrderByNameField();
			}

			case BeneficiariesFieldOrdination.EXCLUSION_DATE: {
				if (
					this.activeTab === BeneficiaryStatusFilter.EXCLUSION ||
					this.activeTab === BeneficiaryStatusFilter.INACTIVE
				) {
					return { dateOfExclusion: this.ordination };
				} else {
					return this.resetOrderByToNameFieldAndGetQuery();
				}
			}

			case BeneficiariesFieldOrdination.INACTIVATION_DATE: {
				if (this.activeTab === BeneficiaryStatusFilter.INACTIVE) {
					return { inactivedAt: this.ordination };
				} else {
					return this.resetOrderByToNameFieldAndGetQuery();
				}
			}

			default: {
				throw new Error(
					`Coluna de ordenação de beneficiários inválida: ${this.sortedField}`,
				);
			}
		}
	}

	getOrderByNameField():
		| { name: SortingType | null }
		| { user: { name: SortingType | null } } {
		if (
			this.activeTab === BeneficiaryStatusFilter.EXCLUSION ||
			this.activeTab === BeneficiaryStatusFilter.INACTIVE
		) {
			return { name: this.ordination };
		} else {
			return { user: { name: this.ordination } };
		}
	}

	hasBeneficiaries(): boolean {
		return this.beneficiaries?.filteredData?.length > 0;
	}

	isBeneficiaryHolder(beneficiary: OrgBeneficiary): boolean {
		return (beneficiary.type_kinship as TypeKinship) === TypeKinship.TITULAR;
	}

	ngAfterViewInit(): void {
		this.beneficiaries.sort = this.sort;
		this.beneficiaries.paginator = this.paginator;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(fetchOrgBeneficiariesClear());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id }) => {
			if (id) {
				this.orgId = id;
				this.clearPaginator();
				this.resetOrderByToNameFieldAndGetQuery();
				this.getBeneficiaries();
			}
		});

		this.activatedRoute.queryParams.subscribe((params) => {
			this.setTabsBeneficiary(
				!params.statusSeguradora
					? BeneficiaryPendencyStatusTranslatedEnum.ACTIVE
					: params.statusSeguradora,
			);
			this.insuranceStatus = params.statusSeguradora;

			this.clearPaginator();
			this.getBeneficiaries();
		});

		const session: AuthenticationLoginResponseDto | null =
			this.authenticationsService.getSession();

		if (session?.org_id === this.sanusId) {
			this.isSanusMode = true;
		}

		this.store
			.select(selectXlsxUrl)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url && !this.preventMultipleDownloads) {
					this.downloadFile(url);
					this.store.dispatch(clearBulkFileUrl());
				}
			});

		this.userEmail = session?.email || '';

		this.store
			.select(selectOrgBeneficiariesDataURL)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url && !this.preventMultipleDownloads) {
					this.downloadFile(url);
					this.store.dispatch(clearOrgBeneficiariesDataURL());
				}
			});

		combineLatest([
			this.store.select(selectOrgUploadFileResult),
			this.store.select(selectBulkActionId),
		])
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(([result, bulkActionId]) => {
				if (result === FileUploadResult.SUCCESS && bulkActionId) {
					const ref = this.dialog.open(BulkActionDialogComponent);
					ref.afterClosed().subscribe((navigate) => {
						if (navigate) {
							this.router.navigate([
								'organizacoes',
								this.orgId,
								'acoes-em-massa',
								bulkActionId,
							]);
						}
					});
					this.store.dispatch(clearBulkUploadResult());
				}
			});
	}

	onActiveDataDownload(): void {
		this.preventMultipleDownloads = false;
		this.store.dispatch(
			fetchOrgBeneficiariesDataURLStart({ orgId: this.orgId }),
		);
	}

	onChangePage(event: PageEvent): void {
		if (event.pageSize !== this.tablePerPage && this.paginator?.pageIndex) {
			this.paginator.pageIndex = 0;
		}

		this.tablePerPage = event.pageSize;
		this.getBeneficiaries();
	}

	onClearSearch(): void {
		this.searchForm.patchValue({ sort: SortingType.ASCENDING, term: null });
		this.getBeneficiaries();
		this.searchButton = true;
		this.isSearchButtonDisabled = true;
		this.getWithSearchTerm = false;
		this.clearPaginator();
	}

	onEditBeneficiary(id: string): void {
		if (this.orgId) {
			void this.router.navigateByUrl(
				`organizacoes/${this.orgId}/beneficiarios/${id}`,
				{
					state: { insuranceStatus: this.insuranceStatus },
				},
			);
		}
	}

	onFileDownload(actionType: TemplateFileType): void {
		this.preventMultipleDownloads = false;
		this.store.dispatch(
			fetchBulkActionFileUrlStart({ actionType, orgId: this.orgId }),
		);
	}

	onFileUpload(actionType: TemplateFileType, event: any): void {
		const request: FormData = new FormData();

		if (this.validFile(event.target.files.item(0))) {
			request.append('excel', event.target.files.item(0));
			request.append('email', this.userEmail);
			request.append('organizationId', this.orgId);
			this.store.dispatch(uploadBulkActionFileStart({ actionType, request }));
			this.clearFileInputs();
		}
	}

	onNewBeneficiary(): void {
		this.dialogRef = this.dialog.open(BeneficiaryTypeModalComponent, {
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result: number) => {
			if (result) {
				if (result === 1) {
					if (this.orgId) {
						void this.router.navigate([
							'organizacoes',
							this.orgId,
							'beneficiarios',
							'cadastro',
						]);
					}
				} else {
					this.openDependentModal();
				}
			}
		});
	}

	onPendencyDialogOpen(
		event: Event,
		id: string,
		status: BeneficiaryPendency,
	): void {
		event.stopPropagation();
		if (this.shouldOpenPendencyDialog(status)) {
			this.dialogRef = this.dialog.open(PendenciesModalComponent, {
				data: {
					beneficiaryId: id,
					sanusMode: this.isSanusMode,
					status: this.activeTab,
				},
				disableClose: true,
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.getBeneficiaries();
				}
			});
		}
	}

	onSearchKeyUp(evt: KeyboardEvent): void {
		const term = this.searchForm.get('term')!.value;

		if (evt.key === 'Enter' && term?.length) {
			this.getBeneficiaries({ hideSearchBtn: true });
		} else {
			if (term?.length) {
				this.searchButton = true;
				this.isSearchButtonDisabled = false;
			} else {
				this.isSearchButtonDisabled = true;
			}
		}
	}

	async onSortData(sortState: Sort): Promise<void> {
		const currentSortedField = sortState.active as BeneficiariesFieldOrdination;

		if (currentSortedField !== this.sortedField) {
			this.sortedField = currentSortedField;

			this.ordination = SortingType.ASCENDING;
			this.sort.direction = SortingType.ASCENDING;
		} else {
			this.ordination = sortState.direction as SortingType;
		}

		this.clearPaginator();

		this.getBeneficiaries();

		if (sortState?.direction) {
			void this.liveAnnouncer.announce(
				`Ordenado ${sortState?.direction}endente`,
			);
		} else {
			await this.liveAnnouncer.announce('Removida ordenação');
		}
	}

	onTabClick(cdt: string): void {
		const selectedTab = cdt as BeneficiaryStatusFilter;

		if (
			!(
				selectedTab !== BeneficiaryStatusFilter.ACTIVE &&
				selectedTab !== BeneficiaryStatusFilter.INACTIVE &&
				selectedTab !== BeneficiaryStatusFilter.EXCLUSION &&
				selectedTab !== BeneficiaryStatusFilter.INCLUSION
			)
		) {
			this.setOrderByToNameField();
			this.router.navigate(['organizacoes', this.orgId, 'beneficiarios'], {
				queryParams: {
					statusSeguradora: this.getBeneficiaryPendencyStatus(cdt),
				},
			});
		}
	}

	openDependentModal(): void {
		this.dialogRef = this.dialog.open(AddDependentModalComponent, {
			data: this.orgId,
			disableClose: true,
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.onTabClick(BeneficiaryStatusFilter.INCLUSION);
			}
		});
	}

	phoneMask(value: string): string {
		return value ? PhoneUtils.phoneMask(value) : '';
	}

	processStatusCssClass(pendency?: BeneficiaryPendency): string {
		if (this.activeTab === BeneficiaryStatusFilter.INACTIVE) {
			return BeneficiaryStatusFilter.INACTIVE;
		}

		if (this.activeTab === BeneficiaryStatusFilter.EXCLUSION) {
			return pendency === BeneficiaryPendency.TO_BE_INACTIVATED
				? BeneficiaryPendency.FIELDS_REQUIRED
				: pendency || BeneficiaryPendency.FIELDS_REQUIRED;
		}

		return pendency || 'COMPLETE';
	}

	processedStatus(status: BeneficiaryPendency): string {
		if (this.activeTab === BeneficiaryStatusFilter.INACTIVE) {
			return 'Inativo';
		}

		const chip = this.beneChips.find((beneChip) => beneChip.status === status);

		return chip?.label
			? chip.label
			: this.activeTab === BeneficiaryStatusFilter.EXCLUSION
				? 'Aberto'
				: 'Completo';
	}

	resetOrderByToNameFieldAndGetQuery() {
		this.setOrderByToNameField();

		return this.getOrderByNameField();
	}

	resetPagination(): void {
		this.tableTotalPages = 0;
	}

	setOrderByToNameField(): void {
		this.sortedField = BeneficiariesFieldOrdination.NAME;
		this.ordination = SortingType.ASCENDING;
		if (this.sort) {
			this.sort.active = BeneficiariesFieldOrdination.NAME;
			this.sort.direction = SortingType.ASCENDING;
		}
	}

	setTabsBeneficiary(
		statusValue: BeneficiaryPendencyStatusTranslatedEnum,
	): void {
		switch (statusValue) {
			case BeneficiaryPendencyStatusTranslatedEnum.ACTIVE:
				this.activeTab = BeneficiaryStatusFilter.ACTIVE;
				this.selectedLabel = 'ativos';
				this.tableColumnsShown = this.tableColumns.activeAndInactive;
				break;
			case BeneficiaryPendencyStatusTranslatedEnum.INACTIVE:
				this.activeTab = BeneficiaryStatusFilter.INACTIVE;
				this.selectedLabel = 'inativo';
				this.tableColumnsShown = this.tableColumns.inactive;
				break;
			case BeneficiaryPendencyStatusTranslatedEnum.EXCLUSION:
				this.activeTab = BeneficiaryStatusFilter.EXCLUSION;
				this.selectedLabel = 'exclusao';
				this.tableColumnsShown = this.tableColumns.exclusion;
				break;
			case BeneficiaryPendencyStatusTranslatedEnum.INCLUSION:
				this.activeTab = BeneficiaryStatusFilter.INCLUSION;
				this.selectedLabel = 'com pendência';
				this.tableColumnsShown = this.tableColumns.activeAndInactive;
				break;
			default:
				throw new Error(`Não tratado: ${statusValue}`);
		}
		this.clearPaginator();
		this.getBeneficiaries();
	}

	shouldOpenPendencyDialog(status: BeneficiaryPendency | null): boolean {
		if (
			this.activeTab !== BeneficiaryStatusFilter.INCLUSION &&
			this.activeTab !== BeneficiaryStatusFilter.EXCLUSION
		) {
			return false;
		}

		return !!status || this.activeTab === BeneficiaryStatusFilter.EXCLUSION;
	}

	showErrorMessage(message: string): void {
		const dialogRef = this.dialog.open(ErrorDialogComponent, {
			data: message,
			maxHeight: undefined,
			maxWidth: undefined,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.clearPaginator();
				this.getBeneficiaries();
			}
		});
	}

	validFile(file: File): boolean {
		const ext = file?.name.split('.').pop();

		return ext === 'xlsx';
	}
}
